import { DocSection, kbd } from '../../../pages/shell';
import { Link } from 'react-router-dom';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'analyze-then-search', label: 'Analyze, then search' },
  { id: 'three-ways', label: 'Three ways to search' },
  { id: 'local-gpu', label: 'Everything runs locally on your GPU' },
  { id: 'what-it-means', label: 'What this means for you' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Most footage-finding tools make you do the remembering: scrub the timeline, or hope you tagged
          and named the file well enough to find it again later. ClipRadar does the remembering for you.
          It reads what's actually <em>in</em> your footage — the pixels, the words spoken, the faces on
          screen — and lets you describe a moment in plain language to get straight to it.
        </p>
      </DocSection>
      <DocSection id="analyze-then-search" title="Analyze, then search">
        <p>
          ClipRadar works in two steps. First you <span style={kbd}>Analyze</span> a source — a one-time
          pass, on your GPU, that reads the footage and stores what it found. Then you search, and every
          search after that is instant: there's no re-scanning the library and no re-decoding the file.
        </p>
        <p>
          See <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link> for exactly what
          each pass does and where the results are kept.
        </p>
      </DocSection>
      <DocSection id="three-ways" title="Three ways to search">
        <p>
          Once a source is analyzed, you can find a moment in it three ways: <span style={kbd}>Visuals</span> (describe
          what's on screen — reads the pixels), <span style={kbd}>Speech</span> (search transcripts for the
          exact words someone said), and by <strong>person</strong> (filter results to someone you've
          named). <span style={kbd}>Ultra Search</span> fuses all three into one plain-language sentence.
        </p>
        <p>
          Details on each: <Link to="/docs/concepts/visual-search">Visual search</Link>,{' '}
          <Link to="/docs/concepts/speech-search">Speech search</Link>,{' '}
          <Link to="/docs/concepts/people-and-faces">People &amp; faces</Link>, and{' '}
          <Link to="/docs/concepts/ultra-search">Ultra Search</Link>.
        </p>
      </DocSection>
      <DocSection id="local-gpu" title="Everything runs locally on your GPU">
        <p>
          Analysis and search both happen on your machine. There's no cloud inference and no vector
          database — search compares your query directly against every stored embedding, in RAM, on your
          own GPU. Your footage never uploads anywhere.
        </p>
        <p>
          An <strong>NVIDIA GPU (CUDA) is required</strong> for any of this to run — ClipRadar does not
          work without one. See <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link> for
          what your GPU buys you, and <Link to="/docs/concepts/local-and-private">Local &amp; private</Link> for
          the exact, short list of things ClipRadar does send over the network.
        </p>
      </DocSection>
      <DocSection id="what-it-means" title="What this means for you">
        <p>
          In practice: the first time you point ClipRadar at a batch of footage, there's a wait while it
          analyzes on your GPU. After that, search is close to instant, works with no internet connection,
          and doesn't depend on how — or whether — you organized or named your files in the first place.
        </p>
      </DocSection>
    </>
  );
}
